import React, { useState, useEffect, useCallback } from "react";
import Modal from "./Modal";
import { adminApiRequest } from "../utils/adminApi";
import PageLoader from "./PageLoader";

const formatDate = (isoDate) => {
  const [year, month, day] = isoDate.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const StoreClosuresPage = () => {
  const [closures, setClosures] = useState([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingClosure, setEditingClosure] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [closureToDelete, setClosureToDelete] = useState(null);

  const [closedDate, setClosedDate] = useState("");
  const [reason, setReason] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Loading...");
  const [error, setError] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const fetchClosures = useCallback(async () => {
    setLoadingMessage("Fetching closed dates...");
    setIsSubmitting(true);

    try {
      const res = await adminApiRequest({
        script: "get-store-closures",
        data: {},
      });

      setClosures(res?.success ? res.data : []);
    } catch (err) {
      setErrorWithTimeout("Failed to fetch closed dates");
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  useEffect(() => {
    fetchClosures();
  }, [fetchClosures]);

  const setErrorWithTimeout = (errorMessage) => {
    setError(errorMessage);
    if (errorMessage) {
      setTimeout(() => setError(""), 3000);
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  const resetFormState = () => {
    setClosedDate("");
    setReason("");
    setEditingClosure(null);
  };

  const handleCloseModal = () => {
    setShowFormModal(false);
    setShowDeleteModal(false);
    resetFormState();
  };

  const handleAddClick = () => {
    resetFormState();
    setShowFormModal(true);
  };

  const handleEditClosure = (closure) => {
    setEditingClosure(closure);
    setClosedDate(closure.closed_date);
    setReason(closure.reason || "");
    setShowFormModal(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const isEditing = Boolean(editingClosure);

    setErrorWithTimeout("");
    setLoadingMessage(isEditing ? "Updating closed date..." : "Adding closed date...");
    setIsSubmitting(true);

    try {
      const res = await adminApiRequest({
        script: isEditing ? "edit-store-closure" : "add-store-closure",
        data: isEditing
          ? {
              id: editingClosure.id,
              closed_date: closedDate,
              reason: reason.trim(),
            }
          : {
              closed_date: closedDate,
              reason: reason.trim(),
            },
      });

      if (!res?.success) {
        setIsSubmitting(false);
        setErrorWithTimeout(res?.error || "Unexpected error occurred");
        return;
      }

      setIsSubmitting(false);
      handleCloseModal();
      fetchClosures();
      showSuccess(
        isEditing ? "Closed date updated successfully" : "Closed date added successfully"
      );
    } catch (err) {
      setIsSubmitting(false);
      setErrorWithTimeout("Unexpected error occurred.");
    }
  };

  const handleDeleteClosure = (closure) => {
    setClosureToDelete(closure);
    setShowDeleteModal(true);
  };

  const confirmDeleteClosure = async (e) => {
    e.preventDefault();
    setLoadingMessage("Removing closed date...");
    setIsSubmitting(true);

    try {
      const res = await adminApiRequest({
        script: "delete-store-closure",
        data: { id: closureToDelete.id },
      });

      if (!res?.success) {
        setIsSubmitting(false);
        setErrorWithTimeout(res?.error || "Failed to remove closed date.");
        return;
      }

      setIsSubmitting(false);
      setShowDeleteModal(false);
      fetchClosures();
      showSuccess("Closed date removed successfully");
    } catch (err) {
      setIsSubmitting(false);
      setErrorWithTimeout("An error occurred while removing the closed date.");
    }
  };

  const inputClass =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-200";

  return (
    <main className="h-full overflow-y-auto">
      {showSuccessMessage && (
        <div className="fixed right-4 top-4 z-50 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 shadow-lg">
          {successMessage}
        </div>
      )}

      <div className="w-full">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-5 py-4 md:px-6 md:py-5">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-xl font-semibold tracking-tight text-slate-900 md:text-2xl">
                  Store Closures
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                  Mark dates the restaurant is closed. Customers will see a notice on the website.
                </p>
              </div>

              <button
                onClick={handleAddClick}
                className="inline-flex items-center justify-center rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 active:scale-[0.98]"
              >
                Add Closed Date
              </button>
            </div>
          </div>

          <div className="p-4 md:p-6">
            {closures.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-sm text-slate-500">
                No closed dates scheduled.
              </div>
            ) : (
              <div className="space-y-3">
                {closures.map((closure) => (
                  <div
                    key={closure.id}
                    className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 md:px-5"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-base font-semibold text-slate-900">
                        {formatDate(closure.closed_date)}
                      </p>
                      {closure.reason && (
                        <p className="mt-1 text-sm text-slate-500">
                          {closure.reason}
                        </p>
                      )}
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        title="Edit"
                        onClick={() => handleEditClosure(closure)}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
                      >
                        <i className="fas fa-pencil-alt text-sm"></i>
                      </button>
                      <button
                        title="Delete"
                        onClick={() => handleDeleteClosure(closure)}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-rose-200 text-rose-600 transition hover:bg-rose-50"
                      >
                        <i className="fas fa-trash-alt text-sm"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showFormModal && (
        <Modal
          title={editingClosure ? "Edit Closed Date" : "Add Closed Date"}
          onClose={handleCloseModal}
          onSubmit={handleFormSubmit}
          submitButtonText={editingClosure ? "Save Changes" : "Add Closed Date"}
          showCancel={false}
          className="modal-content"
          showSubmitButton={true}
          disableSubmit={isSubmitting}
        >
          <div className="space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-800">
                Date
              </label>
              <input
                type="date"
                value={closedDate}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => setClosedDate(e.target.value)}
                className={inputClass}
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-800">
                Reason (optional)
              </label>
              <input
                type="text"
                value={reason}
                placeholder="e.g. Staff training"
                onChange={(e) => setReason(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
        </Modal>
      )}

      {showDeleteModal && (
        <Modal
          title="Remove Closed Date"
          onClose={handleCloseModal}
          onSubmit={confirmDeleteClosure}
          submitButtonText="Remove"
          showCancel={true}
          showSubmitButton={true}
          className="modal-content delete-modal-content"
          disableSubmit={isSubmitting}
        >
          <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-4">
            <p className="text-sm font-medium text-rose-700">
              Are you sure you want to remove{" "}
              {closureToDelete && formatDate(closureToDelete.closed_date)} from the closed dates?
            </p>
          </div>
        </Modal>
      )}

      {error && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4">
          <div className="w-full max-w-sm rounded-2xl border border-rose-200 bg-white p-5 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-100 text-rose-600">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Error</p>
                <p className="mt-1 text-sm text-slate-600">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {isSubmitting && <PageLoader message={loadingMessage} />}
    </main>
  );
};

export default StoreClosuresPage;
